import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  useTheme,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { ChangeEvent, FocusEvent, useState } from "react";
import { useFormValidation } from "../utils/validation";

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
}

export const ContactForm = ({ open, onClose }: ContactFormProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const { validateField } = useFormValidation();

  const onHoverStyle = {
    "&:hover": {
      "& svg": {
        color: "secondary.main",
      },
    },
  };

  const validateEmail = (email: string): boolean => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const sanitizeInput = (input: string): string => {
    return input.trim().replace(/[<>]/g, "");
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (value.trim() === "") return;

    const error = validateField(name, value);

    if (error !== null) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error || "",
      }));
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    setFormData((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: { [key: string]: string } = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        setIsSubmitting(true);
        const form = e.target as HTMLFormElement;
        const formData = new FormData(form);

        const response = await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(formData as any).toString(),
        });

        if (!response.ok) throw new Error("Form submission failed");

        setSubmitted(true);
        setFormData({ name: "", email: "", message: "" });

        setTimeout(() => {
          handleClose();
          setSubmitted(false);
        }, 3000);
      } catch (error) {
        console.error("Form submission error:", error);
        setErrors((prev) => ({
          ...prev,
          submit: "Failed to send message. Please try again later.",
        }));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    setFormData({ name: "", email: "", message: "" });
    setErrors({});
    onClose();
  };

  const isSubmitDisabled =
    isSubmitting ||
    !!errors.name ||
    !!errors.email ||
    !!errors.message ||
    !!errors.submit ||
    !formData.name ||
    !formData.email ||
    !formData.message;

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              component: motion.div,
              style: { padding: 0 },
              sx: {
                backgroundColor: "background.paper",
                backgroundImage: "none",
              },
            },
          }}
          sx={{
            "& .MuiDialog-paper": {
              opacity: 0,
              y: 100,
              animation: "slideUp 0.3s ease-out forwards",
              "@keyframes slideUp": {
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            },
          }}
        >
          <DialogTitle>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5">Let's Talk</Typography>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <form
            name="contact"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="form-name" value="contact" />
            <input type="hidden" name="bot-field" />
            <DialogContent>
              {submitted && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Thank you for your message! I'll get back to you soon.
                </Alert>
              )}
              {errors.submit && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.submit}
                </Alert>
              )}
              <Box display="flex" flexDirection="column" gap={3}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Name
                  </Typography>
                  <TextField
                    autoFocus
                    required
                    fullWidth
                    id="name"
                    type="text"
                    name="name"
                    placeholder="Your name"
                    autoComplete="contact-name"
                    error={!!errors.name}
                    helperText={errors.name || ""}
                    color={!!errors.name ? "error" : "primary"}
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-describedby={errors.name ? "name-error" : undefined}
                    aria-label="Name"
                    aria-invalid={!!errors.name}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "background.paper",
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Email
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    type="email"
                    name="email"
                    placeholder="Your email"
                    autoComplete="contact-email"
                    error={!!errors.email}
                    helperText={errors.email || ""}
                    color={!!errors.email ? "error" : "primary"}
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-describedby={errors.email ? "email-error" : undefined}
                    aria-label="Email"
                    aria-invalid={!!errors.email}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "background.paper",
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Message
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    id="message"
                    name="message"
                    placeholder="Your message"
                    autoComplete="contact-message"
                    error={!!errors.message}
                    color={!!errors.message ? "error" : "primary"}
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-describedby={
                      errors.message ? "message-error" : undefined
                    }
                    aria-label="Message"
                    aria-invalid={!!errors.message}
                    size="small"
                    multiline
                    rows={4}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "background.paper",
                      },
                    }}
                    helperText={
                      errors.message
                        ? errors.message
                        : `${formData.message.length}/2000`
                    }
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ pr: 3, mb: 3 }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  backgroundColor: "primary",
                  boxShadow: theme.shadows[4],
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="outlined"
                endIcon={<SendIcon />}
                disabled={isSubmitDisabled}
                sx={{
                  backgroundColor: "primary",
                  boxShadow: theme.shadows[4],
                  ...onHoverStyle,
                }}
              >
                {isSubmitting ? "Sending..." : "Send"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </AnimatePresence>
  );
};
